import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { 
  getUnblockRequestsForReview, 
  updateUnblockRequestStatus,
  UnblockRequest 
} from '../services/unblockRequestApi';
import { toast } from 'react-hot-toast';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  Loader2,
  User,
  Mail,
  Calendar,
  FileText
} from 'lucide-react';
import { useRbac } from '../context';

interface UnblockRequestsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId?: string; // Optional: filter by specific user
  onRequestUpdated?: () => void; // Callback to refresh count in parent
}

export const UnblockRequestsModal: React.FC<UnblockRequestsModalProps> = ({
  isOpen,
  onClose,
  userId,
  onRequestUpdated
}) => {
  const { user: currentUser } = useRbac();
  const [requests, setRequests] = useState<UnblockRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<UnblockRequest | null>(null);
  const [reviewNotesMap, setReviewNotesMap] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('pending');

  useEffect(() => {
    if (isOpen) {
      fetchRequests();
    }
  }, [isOpen, statusFilter]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await getUnblockRequestsForReview(1, 100, statusFilter === 'all' ? undefined : statusFilter);
      if (response.success) {
        let filteredRequests = response.data.requests;
        // If userId is provided, filter by that user
        if (userId) {
          filteredRequests = filteredRequests.filter(
            req => req.requestedBy._id === userId
          );
        }
        setRequests(filteredRequests);
      }
    } catch (error: any) {
      console.error('Failed to fetch unblock requests:', error);
      toast.error(error.message || 'Failed to fetch unblock requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (request: UnblockRequest) => {
    setSelectedRequest(request);
    setIsSubmitting(true);
    try {
      const notes = reviewNotesMap[request._id] || '';
      await updateUnblockRequestStatus(request._id, 'approved', notes || undefined);
      toast.success('Unblock request approved successfully');
      setSelectedRequest(null);
      setReviewNotesMap(prev => {
        const newMap = { ...prev };
        delete newMap[request._id];
        return newMap;
      });
      fetchRequests();
      if (onRequestUpdated) {
        onRequestUpdated();
      }
    } catch (error: any) {
      console.error('Failed to approve request:', error);
      toast.error(error.message || 'Failed to approve request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReject = async (request: UnblockRequest) => {
    const notes = reviewNotesMap[request._id] || '';
    if (!notes.trim()) {
      toast.error('Please provide review notes for rejection');
      return;
    }
    setSelectedRequest(request);
    setIsSubmitting(true);
    try {
      await updateUnblockRequestStatus(request._id, 'rejected', notes);
      toast.success('Unblock request rejected');
      setSelectedRequest(null);
      setReviewNotesMap(prev => {
        const newMap = { ...prev };
        delete newMap[request._id];
        return newMap;
      });
      fetchRequests();
      if (onRequestUpdated) {
        onRequestUpdated();
      }
    } catch (error: any) {
      console.error('Failed to reject request:', error);
      toast.error(error.message || 'Failed to reject request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Unblock Requests</DialogTitle>
          <DialogDescription>
            Review and manage unblock requests from users
          </DialogDescription>
        </DialogHeader>

        {/* Status Filter */}
        <div className="flex gap-2 mb-4">
          <Button
            variant={statusFilter === 'pending' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('pending')}
          >
            Pending
          </Button>
          <Button
            variant={statusFilter === 'approved' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('approved')}
          >
            Approved
          </Button>
          <Button
            variant={statusFilter === 'rejected' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('rejected')}
          >
            Rejected
          </Button>
          <Button
            variant={statusFilter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter('all')}
          >
            All
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
          </div>
        ) : requests.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No unblock requests found
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((request) => (
              <div
                key={request._id}
                className="border rounded-lg p-4 space-y-3"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-500" />
                        <span className="font-semibold">{request.requestedBy.username}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-600">{request.requestedBy.email}</span>
                      </div>
                      {getStatusBadge(request.status)}
                    </div>
                    {request.reason && (
                      <div className="mt-2 p-3 bg-gray-50 rounded-md">
                        <div className="flex items-start gap-2">
                          <FileText className="w-4 h-4 text-gray-500 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-gray-700">Reason:</p>
                            <p className="text-sm text-gray-600">{request.reason}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>Requested: {new Date(request.createdAt).toLocaleDateString()}</span>
                      </div>
                      {request.reviewedAt && (
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          <span>Reviewed: {new Date(request.reviewedAt).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                    {request.reviewNotes && (
                      <div className="mt-2 p-2 bg-blue-50 rounded-md">
                        <p className="text-xs font-medium text-blue-700">Review Notes:</p>
                        <p className="text-xs text-blue-600">{request.reviewNotes}</p>
                      </div>
                    )}
                  </div>
                </div>

                {request.status === 'pending' && (
                  <div className="flex gap-2 pt-2 border-t">
                    <div className="flex-1">
                      <Textarea
                        placeholder="Add review notes (required for rejection)..."
                        value={reviewNotesMap[request._id] || ''}
                        onChange={(e) => setReviewNotesMap(prev => ({ ...prev, [request._id]: e.target.value }))}
                        className="min-h-[80px]"
                      />
                    </div>
                    <div className="flex flex-col gap-2">
                      <Button
                        onClick={() => handleApprove(request)}
                        disabled={isSubmitting && selectedRequest?._id === request._id}
                        className="bg-green-600 hover:bg-green-700"
                        size="sm"
                      >
                        {isSubmitting && selectedRequest?._id === request._id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Approve
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => handleReject(request)}
                        disabled={(isSubmitting && selectedRequest?._id === request._id) || !(reviewNotesMap[request._id] || '').trim()}
                        variant="destructive"
                        size="sm"
                      >
                        {isSubmitting && selectedRequest?._id === request._id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <XCircle className="w-4 h-4 mr-2" />
                            Reject
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

